import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
import DreamnMatchLeagues from "../DreamnMatchLeagues";
import DreamnMatch from "../DreamnMatch";
import DreamnSetting from "../DreamnMatch/DreamnSetting";
import DreamnDateTabs from "../DreamnDateTabs";
import LoadMoreButton from "../LoadMoreButton";
import LeagueFilterV12 from "../LeagueFilterV12";
import { BANG_KEO_PAGE_LIMIT } from "@/contants";
import {
  filterLeagueGroupsByMatch,
  filterLeaguesBySearchTerm,
  groupMatchesByLeague,
  isLiveMatchStatus,
  isUpcomingTabStatus,
} from "./matchListHelpers";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV12 = ({ type = "all" }) => {

  const {
    colorPrimary,
    colorSecondary,
    colorBg,
    colorText,
    colorForeground,
    colorBorder,
    colorAccent,
    colorTextMuted,
    colorOnPrimary,
    colorBtnBorder,
    colorRowEven,
    colorRowOdd,
    hasDataConfig,
  } = useCompConfig();

  const themeStyle = hasDataConfig
    ? {
        "--theme-primary": colorPrimary,
        "--theme-highlight": colorSecondary,
        "--theme-bg": colorBg,
        "--theme-text": colorText,
        "--theme-foreground": colorForeground,
        ...(colorBorder != null && { "--theme-border": colorBorder }),
        ...(colorAccent != null && { "--theme-accent": colorAccent }),
        ...(colorTextMuted != null && { "--theme-text-muted": colorTextMuted }),
        ...(colorOnPrimary != null && { "--theme-on-primary": colorOnPrimary }),
        ...(colorBtnBorder != null && { "--theme-btn-border": colorBtnBorder }),
        ...(colorRowEven != null && { "--theme-row-even": colorRowEven }),
        ...(colorRowOdd != null && { "--theme-row-odd": colorRowOdd }),
      }
    : {};

  const [firstLoad, setFirstLoad] = useState(true);
  const today = useMemo(() => dayjs().tz(TZ), []);
  const todayString = today.format("YYYY-MM-DD");

  const [selectedDate, setSelectedDate] = useState(todayString);
  const [searchTerm, setSearchTerm] = useState("");
  const [subFilter, setSubFilter] = useState("all"); // "all", "live", "upcoming"
  const [displayLimit, setDisplayLimit] = useState(20);

  const isTodayOrLive = selectedDate === todayString || selectedDate === "live";

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;

  const status =
    type === "ketqua"
      ? "end"
      : isTodayOrLive
        ? ""
        : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    let result = baseLeaguesWithMatches;

    const q = searchTerm.trim();
    if (q) {
      result = filterLeaguesBySearchTerm(result, q.toLowerCase());
    }

    if (isTodayOrLive) {
      if (subFilter === "live") {
        result = filterLeagueGroupsByMatch(result, (m) => isLiveMatchStatus(m.status));
      } else if (subFilter === "upcoming") {
        result = filterLeagueGroupsByMatch(result, (m) => isUpcomingTabStatus(m.status));
      }
    }

    return result;
  }, [baseLeaguesWithMatches, searchTerm, subFilter, isTodayOrLive]);

  const allFilteredMatches = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
        (matches || []).map((m) => ({ ...m, league })),
      ),
    [filteredLeaguesWithMatches],
  );

  const visibleMatches = useMemo(
    () => allFilteredMatches.slice(0, displayLimit),
    [allFilteredMatches, displayLimit],
  );

  const groupedVisibleMatches = useMemo(
    () => groupMatchesByLeague(visibleMatches),
    [visibleMatches],
  );

  useEffect(() => {
    // Auto-fetch if filtered results are below display limit
    if (
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [
    firstLoad,
    selectedDate,
    loading,
    filteredLeaguesWithMatches,
    todayString,
  ]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setDisplayLimit(20); // Reset display limit
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setFirstLoad(false);
    setDisplayLimit(20); // Reset display limit
  };

  return (
    <div
      className="mx-auto flex min-h-screen flex-col gap-3 rounded-xl px-2 md:px-2 py-3 bg-[var(--kbv-bg-page)] text-[15px] max-w-[950px]"
      style={themeStyle}
    >
      <div className="relative overflow-x-auto overflow-y-hidden mr-0.5 mb-4 box-border w-full rounded-xl border border-[var(--kbv-card-border)] bg-[var(--kbv-card-bg)] max-md:mb-0 max-md:rounded-t-[14px] max-md:rounded-b-none max-md:shadow-none max-md:px-0 md:px-1">
        <div className="flex flex-col w-full py-2 gap-y-3">
          <div className="flex w-full min-w-0 items-stretch">
            <DreamnDateTabs
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
            />
          </div>

          {isTodayOrLive && (
            <div className="mt-2 flex w-full min-w-0 flex-row gap-1.5 px-1 md:mt-3 md:gap-2">
              <div
                onClick={() => setSubFilter("all")}
                className={`relative flex min-h-[34px] flex-1 basis-0 cursor-pointer select-none items-center justify-center rounded-lg border-0 px-0.5 text-center text-[12px] font-bold uppercase tracking-tight transition-all duration-300 md:min-h-[44px] md:rounded-xl md:px-1 md:text-[15px] md:tracking-tighter ${subFilter === "all" ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] shadow-md shadow-[var(--theme-primary)]/30" : "bg-transparent text-[var(--theme-text-muted)] hover:bg-[var(--theme-accent)] hover:text-[var(--theme-text)]"}`}
              >
                <span>Tất cả</span>
              </div>
              <div
                onClick={() => setSubFilter("live")}
                className={`relative flex min-h-[34px] flex-1 basis-0 cursor-pointer select-none items-center justify-center rounded-lg border-0 px-0.5 text-center text-[12px] font-bold uppercase tracking-tight transition-all duration-300 md:min-h-[44px] md:rounded-xl md:px-1 md:text-[15px] md:tracking-tighter ${subFilter === "live" ? "bg-red-500 text-white shadow-md shadow-red-500/30" : "bg-transparent text-[var(--theme-text-muted)] hover:bg-[var(--theme-accent)] hover:text-[var(--theme-text)]"}`}
              >
                <span className="inline-flex items-center justify-center gap-1 md:gap-2">
                  <span className="relative inline-flex h-2 w-2 shrink-0 items-center justify-center md:h-2.5 md:w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white md:h-2 md:w-2" />
                  </span>
                  <span className="whitespace-nowrap">Trực tuyến</span>
                </span>
              </div>
              <div
                onClick={() => setSubFilter("upcoming")}
                className={`relative flex min-h-[34px] flex-1 basis-0 cursor-pointer select-none items-center justify-center rounded-lg border-0 px-0.5 text-center text-[12px] font-bold uppercase tracking-tight transition-all duration-300 md:min-h-[44px] md:rounded-xl md:px-1 md:text-[15px] md:tracking-tighter ${subFilter === "upcoming" ? "bg-[var(--theme-primary)] text-[var(--theme-on-primary)] shadow-md shadow-[var(--theme-primary)]/30" : "bg-transparent text-[var(--theme-text-muted)] hover:bg-[var(--theme-accent)] hover:text-[var(--theme-text)]"}`}
              >
                <span>Sắp đá</span>
              </div>
            </div>
          )}
          <div className="mx-2 md:mx-1 my-3 px-2 py-2 border border-[var(--theme-border)] rounded-sm bg-[var(--theme-row-even)] shadow-sm transition-all duration-200">
            <div className="flex flex-row items-center justify-between w-full gap-2">
              <div className="w-[70%] md:w-[85%]">
                <LeagueFilterV12
                  searchTerm={searchTerm}
                  onSearchChange={handleSearchChange}
                  className="h-full w-full"
                />
              </div>
              <div className="flex-1 flex justify-end">
                <DreamnSetting />
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && <Loading />}
      {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
      {!loading && (
        <>
          <div className="rounded-lg">
            {groupedVisibleMatches.length === 0 ? (
              <NoData
                message={
                  isLoadingMore
                    ? "Đang tìm thêm trận đấu phù hợp..."
                    : "Không tìm thấy trận đấu theo tiêu chí lọc"
                }
              />
            ) : (
              groupedVisibleMatches.map(({ league, matches }, leagueIndex) => (
                <DreamnMatchLeagues
                  key={league?.id || `league-${leagueIndex}`}
                  league={league || {}}
                  matches={matches}
                  render={(match, idx, total) => (
                    match ? <DreamnMatch key={match.id || `${league?.id || leagueIndex}-${match?.matchTime || Math.random()}`} match={match} index={idx} isLast={idx === total - 1} /> : null
                  )}
                />
              ))
            )}
          </div>
          <LoadMoreButton
            isLoading={isLoadingMore}
            onClick={() => setDisplayLimit((prev) => prev + 20)}
            loadingComponent={<Loading />}
            hasMore={allFilteredMatches.length > displayLimit || hasMore}
          />
        </>
      )}
    </div>
  );
};

export default KbvTiLeV12;
